/**
 * Space-Entity Store
 *
 * Per-space LRU cache of space-scoped entities, populated via the same
 * two-stage fetch the agent runtime uses (entityService.getEntitiesForSpace).
 *
 *   - Memory cache: up to MAX_CACHED_SPACES spaces (LRU).
 *   - IDB persistence: per-space JSON snapshot in
 *     `gui/src/services/idb/spaceEntityIDB.ts` (stale-while-revalidate paint
 *     before the REST burst returns).
 *   - Source of truth for sidebar / Cmd+K / app stores reading space data.
 *   - Cleared on org switch.
 */

import { create } from 'zustand';
import { entityService } from '../services/entityService';
import { getOrgEdgesSnapshot, waitForOrgSync } from './orgYDocStore';
import { useDataspaceTreeStore } from './dataspaceTreeStore';
import { useDataspaceContextStore, waitForDataspaceContext } from './dataspaceContextStore';
import { registerScopeAdapter, notifyChange as notifyRegistryChange } from './entityRegistry';
import { readSnapshot, writeSnapshot, patchEntity as idbPatchEntity, deleteSnapshot } from '../services/idb/spaceEntityIDB';
import { createFileLogger } from '../utils/logger';
import type { BaseEntityType } from '../types/entities';

const log = createFileLogger('SPACE_ENTITY_STORE', {enabled: true});

const MAX_CACHED_SPACES = 8;
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export interface SpaceCacheEntry {
  spaceId: string;
  entities: BaseEntityType[];
  entityById: Map<string, BaseEntityType>;
  fetchedAt: number;
  source: 'idb' | 'rest';
}

interface SpaceEntityState {
  caches: Map<string, SpaceCacheEntry>;
  loading: Set<string>;
  inflight: Map<string, Promise<void>>;
  bumpVersion: number;
}

interface SpaceEntityActions {
  /** Load entities for `spaceId`. Idempotent across concurrent callers. */
  initializeSpace: (spaceId: string, opts?: { force?: boolean }) => Promise<void>;
  /** Synchronous read of the cached entities for a space. Returns [] if not loaded. */
  getEntities: (spaceId: string) => BaseEntityType[];
  /** Synchronous read of a single entity from the space cache. */
  getEntity: (spaceId: string, entityId: string) => BaseEntityType | undefined;
  /** Patch an entity in the in-memory cache (e.g. when a Y.Doc node changes). */
  patchEntity: (spaceId: string, entityId: string, patch: Partial<BaseEntityType>) => void;
  /** Drop a space from memory. */
  evictSpace: (spaceId: string) => void;
  /** Drop everything (called on org switch). */
  clearAll: () => void;
}

export type SpaceEntityStore = SpaceEntityState & SpaceEntityActions;

function readEdgesSnapshot(): Array<{ parentId: string | null; childId: string }> {
  const orgEdges = getOrgEdgesSnapshot();
  if (orgEdges.length > 0) {
    return orgEdges.map((e) => ({ parentId: e.parentId, childId: e.childId }));
  }
  const treeNodes = useDataspaceTreeStore.getState().nodesById;
  const result: Array<{ parentId: string | null; childId: string }> = [];
  for (const [, node] of Object.entries(treeNodes)) {
    if (!node || !node.id) continue;
    result.push({ parentId: (node as any).parentId ?? null, childId: node.id });
  }
  return result;
}

function buildEntry(spaceId: string, entities: BaseEntityType[], source: SpaceCacheEntry['source']): SpaceCacheEntry {
  const entityById = new Map<string, BaseEntityType>();
  for (const e of entities) {
    if (e._id) entityById.set(e._id, e);
    if (e.id && e.id !== e._id) entityById.set(e.id, e);
  }
  return {
    spaceId,
    entities,
    entityById,
    fetchedAt: Date.now(),
    source,
  };
}

/**
 * Produce a list of entities for a REST refresh commit, preferring any
 * in-memory version whose `updated_at` is newer than the REST version.
 * This prevents a slow background refresh from overwriting renames/updates
 * the user made after the REST fetch began.
 * Entities present only in the current in-memory cache (newly created since
 * the REST fetch started) are appended so they aren't lost.
 */
function mergeRestWithCache(
  restEntities: BaseEntityType[],
  existingCache: SpaceCacheEntry | undefined,
): BaseEntityType[] {
  if (!existingCache) return restEntities;

  const inMemById = existingCache.entityById;
  const seenIds = new Set<string>();

  const result = restEntities.map((restEntity) => {
    const id = restEntity._id || (restEntity as any).id;
    if (!id) return restEntity;
    seenIds.add(id);
    const inMem = inMemById.get(id);
    if (!inMem) return restEntity;
    const restMs = restEntity.updated_at ? new Date(restEntity.updated_at).getTime() : 0;
    const memMs = inMem.updated_at ? new Date(inMem.updated_at).getTime() : 0;
    return memMs > restMs ? inMem : restEntity;
  });

  // Preserve entities that exist in memory but weren't returned by REST
  // (e.g. entities created between the REST fetch start and now).
  for (const inMemEntity of existingCache.entities) {
    const id = inMemEntity._id || (inMemEntity as any).id;
    if (!id || seenIds.has(id)) continue;
    result.push(inMemEntity);
  }

  return result;
}

function lruInsert(
  caches: Map<string, SpaceCacheEntry>,
  entry: SpaceCacheEntry,
): Map<string, SpaceCacheEntry> {
  const next = new Map(caches);
  next.delete(entry.spaceId);
  next.set(entry.spaceId, entry);
  while (next.size > MAX_CACHED_SPACES) {
    const oldestKey = next.keys().next().value as string | undefined;
    if (!oldestKey) break;
    next.delete(oldestKey);
  }
  return next;
}

export const useSpaceEntityStore = create<SpaceEntityStore>((set, get) => ({
  caches: new Map(),
  loading: new Set(),
  inflight: new Map(),
  bumpVersion: 0,

  initializeSpace: async (spaceId: string, opts: { force?: boolean } = {}) => {
    if (!spaceId) return;

    const existingInflight = get().inflight.get(spaceId);
    if (existingInflight && !opts.force) {
      return existingInflight;
    }

    const promise = (async () => {
      const t0 = performance.now();

      try {
        set((s) => {
          const loading = new Set(s.loading);
          loading.add(spaceId);
          return { loading };
        });

        // Cold-reload guard: `dataspaceContextStore.currentContext` is not
        // persisted, so on the first render after a hard reload the orgId is
        // null until App.tsx finishes its `/user/context` fetch. If we
        // captured that null value and then the cross-org guard below tripped
        // the moment the real orgId landed, the REST result would be
        // silently dropped and the space cache would stay empty. Waiting
        // here resolves immediately when context is already populated.
        let currentDataspaceId =
          useDataspaceContextStore.getState().currentContext.orgId ?? null;
        if (currentDataspaceId === null) {
          currentDataspaceId = await waitForDataspaceContext(5000);
        }

        // Re-reads the live dataspace at every async boundary. If the user
        // switched dataspaces while this initializeSpace was running, the
        // captured `currentDataspaceId` no longer matches and we must abandon
        // any pending state mutations to avoid leaking previous-org entities
        // into the now-active org's cache.
        const liveDataspaceId = (): string | null =>
          useDataspaceContextStore.getState().currentContext.orgId ?? null;
        const isStale = (): boolean => liveDataspaceId() !== currentDataspaceId;

        // Stage 0 — paint from IDB if a fresh-enough snapshot exists for the
        // current dataspace. The REST refresh below is fire-and-forget unless
        // the cache is missing/stale, in which case we await it.
        const snapshot = await readSnapshot(spaceId);
        if (isStale()) {
          log.info(
            `[SpaceEntityStore] initializeSpace=${spaceId} discarded IDB read — dataspace switched mid-fetch`,
          );
          return;
        }
        const cacheUsable =
          !!snapshot &&
          snapshot.meta.dataspaceId === currentDataspaceId &&
          Date.now() - snapshot.meta.fetchedAt <= STALE_AFTER_MS;

        if (cacheUsable && snapshot) {
          const idbEntry = buildEntry(spaceId, snapshot.entities, 'idb');
          set((s) => ({
            caches: lruInsert(s.caches, idbEntry),
            bumpVersion: s.bumpVersion + 1,
          }));
          log.info(
            `[SpaceEntityStore] 🪟 initializeSpace=${spaceId} idbHit count=${snapshot.entities.length} fetchedAt=${snapshot.meta.fetchedAt}`,
          );
        }

        // Stage 1 — fire the space-scoped REST query immediately; do NOT
        // wait for the org Y.Doc handshake. Stage 1 only needs the spaceId
        // and the user's current dataspace context, both of which are
        // synchronously available. Painting Stage 1 right away lets the
        // sidebar populate from the live database within ~150–300ms even on
        // a hard reload, instead of blocking ~3–8s for the Y.Doc handshake.
        // Resolves with the Stage 1 result, or null if the dataspace switched
        // mid-fetch (stale) and the work was discarded. The return type is
        // inferred so stage1Entities keeps fetchSpaceStage1's exact element type.
        const stage1Promise = (async () => {
          const stage1Entities = await entityService.fetchSpaceStage1(spaceId);
          if (isStale()) {
            log.info(
              `[SpaceEntityStore] initializeSpace=${spaceId} discarded Stage 1 — dataspace switched mid-fetch`,
            );
            return null;
          }
          // Paint Stage 1 immediately ONLY when there's nothing usable in IDB.
          // Cold-load (cacheUsable=false): the user sees an empty sidebar
          // until something paints, so Stage 1 alone is a strict win.
          // Warm-load (cacheUsable=true): the IDB paint above already showed
          // Stage 1 + Stage 2 from the previous session, including any
          // cross-space refs (image-studio variants etc.) whose spaceId
          // doesn't match. Painting Stage 1 alone here would *drop* those
          // refs for the duration of the Y.Doc handshake (~3–8s), then
          // re-paint them when Stage 2 returns. We avoid that flicker by
          // committing only at Stage 2 completion when warm.
          const msToStage1Paint = performance.now() - t0;
          if (!cacheUsable) {
            set((s) => {
              const stage1Merged = mergeRestWithCache(stage1Entities, s.caches.get(spaceId));
              return {
                caches: lruInsert(s.caches, buildEntry(spaceId, stage1Merged, 'rest')),
                bumpVersion: s.bumpVersion + 1,
              };
            });
            log.info(
              `[SpaceEntityStore] 🪜 initializeSpace=${spaceId} stage1Painted count=${stage1Entities.length} ms=${msToStage1Paint.toFixed(0)}`,
            );
          } else {
            log.info(
              `[SpaceEntityStore] 🪜 initializeSpace=${spaceId} stage1Ready (warm — paint deferred until Stage 2 to preserve cross-space refs) count=${stage1Entities.length} ms=${msToStage1Paint.toFixed(0)}`,
            );
          }
          return { stage1Entities, msToStage1Paint };
        })();

        // Stage 2 — wait for the org Y.Doc to sync (or fall back to the
        // dataspaceTreeStore snapshot) so we can BFS the edge graph and pull in
        // any cross-space references reachable from this space.
        //
        // CRITICAL: Stage 2 is a background enhancement and MUST NOT gate the
        // space's first paint. The org Y.Doc room for a brand-new org is cold —
        // its initial WS sync (reseed from MongoDB) can take seconds — so
        // awaiting it here previously stalled cold loads for the full org-sync
        // budget before the sidebar could even render. Stage 2 commits its merge
        // reactively via set()+bumpVersion, so a late result still flows to the
        // UI. We run it detached for BOTH warm and cold loads; the returned
        // promise resolves once Stage 1 is ready.
        //
        // Why cross-space refs matter: images persisted by
        // /image-studio/persist-variant leave entities.spaceId=null and rely on
        // the dataspace edge for hierarchy. Without this BFS the sidebar
        // wouldn't see them. We accept the eventual-consistency window
        // (Stage 1 paint → Stage 2 merge) because Stage 1 covers the vast
        // majority of entities in a typical space.
        const stage2Promise = (async () => {
          const stage1Result = await stage1Promise;
          if (!stage1Result) return;
          const { stage1Entities, msToStage1Paint } = stage1Result;

          const ORG_SYNC_BUDGET_MS = 8000;
          const orgSyncDeadline = Date.now() + ORG_SYNC_BUDGET_MS;
          while (Date.now() < orgSyncDeadline) {
            const remaining = Math.max(0, orgSyncDeadline - Date.now());
            try {
              await waitForOrgSync(remaining);
              break;
            } catch (err) {
              if (isStale()) return;
              const transient = String(err).includes('Destroyed');
              if (!transient) {
                log.warn(
                  `[SpaceEntityStore] initializeSpace=${spaceId} Stage 2 Y.Doc sync timed out — proceeding with whatever edges readEdgesSnapshot can supply`,
                );
                break;
              }
              await Promise.resolve();
            }
          }
          if (isStale()) {
            log.info(
              `[SpaceEntityStore] initializeSpace=${spaceId} discarded post-sync — dataspace switched mid-fetch`,
            );
            return;
          }
          const edges = readEdgesSnapshot();
          const stage2Entities = await entityService.fetchSpaceStage2(spaceId, stage1Entities, edges);
          if (isStale()) {
            log.info(
              `[SpaceEntityStore] initializeSpace=${spaceId} discarded Stage 2 — dataspace switched mid-fetch`,
            );
            return;
          }
          const merged = stage2Entities.length === 0
            ? stage1Entities
            : [...stage1Entities, ...stage2Entities];
          // Always commit the merged result when the warm-load Stage 1 paint
          // was deferred (cacheUsable=true) — otherwise fresh Stage 1 data
          // never reaches memory in the no-cross-space-refs case. For the
          // cold-load path we only need to commit when Stage 2 actually
          // adds entities (Stage 1 was already painted above).
          const shouldCommitMerged = cacheUsable || stage2Entities.length > 0;
          let snapshotEntities: BaseEntityType[] = merged;
          if (shouldCommitMerged) {
            set((s) => {
              const finalMerged = mergeRestWithCache(merged, s.caches.get(spaceId));
              snapshotEntities = finalMerged;
              return {
                caches: lruInsert(s.caches, buildEntry(spaceId, finalMerged, 'rest')),
                bumpVersion: s.bumpVersion + 1,
              };
            });
          }
          await writeSnapshot(spaceId, snapshotEntities, currentDataspaceId);
          const msToComplete = performance.now() - t0;
          log.info(
            `[SpaceEntityStore] ✅ initializeSpace=${spaceId} count=${merged.length} ms=${msToComplete.toFixed(0)} source=rest`,
          );
          // Telemetry — fire-and-forget so a slow ingest can't block the
          // sidebar paint flow.
          import('../services/analytics/analytics').then(({ analytics }) => {
            analytics.track('space_sidebar_loaded_ms', {
              space_id: spaceId,
              org_id: currentDataspaceId,
              ms_to_stage1_paint: Math.round(msToStage1Paint),
              ms_to_complete: Math.round(msToComplete),
              stage1_entity_count: stage1Entities.length,
              stage2_entity_count: stage2Entities.length,
              source: cacheUsable ? 'idb' : 'rest',
            });
          }).catch(() => {});
        })();

        // Stage 2 always runs detached — never let the org-sync handshake block
        // first paint or throw out of stale-while-revalidate.
        stage2Promise.catch((err) =>
          log.warn(`[SpaceEntityStore] background Stage 2 for ${spaceId} failed`, err),
        );

        // First paint is ready once Stage 1 is committed (cold load). On a warm
        // load the IDB paint above already covered first paint, so we don't wait
        // on Stage 1 at all.
        if (!cacheUsable) {
          await stage1Promise;
        }
      } catch (err) {
        log.error(`[SpaceEntityStore] ❌ initializeSpace=${spaceId} failed`, err);
        throw err;
      } finally {
        set((s) => {
          const loading = new Set(s.loading);
          loading.delete(spaceId);
          const inflight = new Map(s.inflight);
          inflight.delete(spaceId);
          return { loading, inflight };
        });
      }
    })();

    set((s) => {
      const inflight = new Map(s.inflight);
      inflight.set(spaceId, promise);
      return { inflight };
    });

    return promise;
  },

  getEntities: (spaceId: string) => {
    const entry = get().caches.get(spaceId);
    return entry?.entities ?? [];
  },

  getEntity: (spaceId: string, entityId: string) => {
    return get().caches.get(spaceId)?.entityById.get(entityId);
  },

  patchEntity: (spaceId: string, entityId: string, patch: Partial<BaseEntityType>) => {
    const entry = get().caches.get(spaceId);
    if (!entry) return;
    const existing = entry.entityById.get(entityId);
    if (!existing) return;
    const updated: BaseEntityType = { ...existing, ...patch };
    const nextById = new Map(entry.entityById);
    nextById.set(entityId, updated);
    if (updated.id && updated.id !== entityId) nextById.set(updated.id, updated);
    const nextList = entry.entities.map((e) =>
      e._id === entityId || e.id === entityId ? updated : e,
    );
    const nextEntry: SpaceCacheEntry = {
      ...entry,
      entities: nextList,
      entityById: nextById,
    };
    set((s) => ({
      caches: lruInsert(s.caches, nextEntry),
      bumpVersion: s.bumpVersion + 1,
    }));
  },

  evictSpace: (spaceId: string) => {
    set((s) => {
      const next = new Map(s.caches);
      next.delete(spaceId);
      return { caches: next, bumpVersion: s.bumpVersion + 1 };
    });
  },

  clearAll: () => {
    set({ caches: new Map(), bumpVersion: 0, loading: new Set(), inflight: new Map() });
  },
}));

// Stable reference for the empty-space fallback.
// Zustand's useSyncExternalStore compares snapshots with Object.is, so
// returning a new [] literal on every call when spaceId is null/undefined
// would make every render look "changed", causing an infinite re-render loop.
const EMPTY_ENTITIES: BaseEntityType[] = [];

/**
 * React hook — returns the cached entities for a space.
 * Callers should ensure initializeSpace has been kicked off (e.g. in
 * SpacePageWrapper). This hook intentionally does NOT auto-fetch to keep
 * render trees deterministic and avoid duplicate inflight requests.
 */
export function useSpaceEntities(spaceId: string | null | undefined): BaseEntityType[] {
  return useSpaceEntityStore((s) =>
    spaceId ? (s.caches.get(spaceId)?.entities ?? EMPTY_ENTITIES) : EMPTY_ENTITIES,
  );
}

export function useSpaceEntity(
  spaceId: string | null | undefined,
  entityId: string | null | undefined,
): BaseEntityType | undefined {
  return useSpaceEntityStore((s) =>
    spaceId && entityId ? s.caches.get(spaceId)?.entityById.get(entityId) : undefined,
  );
}

/**
 * Apply an upsert from the registry's live-update bus into every cache that
 * already holds the entity, plus the entity's own space cache when present.
 *
 * Cross-space references: a single entity can appear in multiple cached
 * spaces (e.g. a Doc linked from Space A's tree but living in Space B's
 * spaceId). We update every cache that has it; new "add" actions are only
 * placed in the cache for the entity's own spaceId.
 */
function _applyUpsertToCaches(entity: BaseEntityType): void {
  const entityId = entity._id || (entity as any).id;
  if (!entityId) return;
  const state = useSpaceEntityStore.getState();
  const incomingSpaceId: string | null = (entity as any).spaceId ?? null;
  let mutated = false;
  const nextCaches = new Map(state.caches);

  for (const [spaceId, entry] of nextCaches) {
    const existing = entry.entityById.get(entityId);
    const ownsThisSpace = spaceId === incomingSpaceId;
    if (!existing && !ownsThisSpace) continue;

    const merged: BaseEntityType = existing
      ? ({ ...existing, ...entity, skeleton: { ...((existing as any).skeleton ?? {}), ...((entity as any).skeleton ?? {}) } } as BaseEntityType)
      : (entity as BaseEntityType);

    const nextById = new Map(entry.entityById);
    nextById.set(entityId, merged);
    if ((merged as any).id && (merged as any).id !== entityId) {
      nextById.set((merged as any).id, merged);
    }
    const nextList = existing
      ? entry.entities.map((e) => (e._id === entityId || (e as any).id === entityId ? merged : e))
      : [...entry.entities, merged];
    nextCaches.set(spaceId, { ...entry, entities: nextList, entityById: nextById });
    idbPatchEntity(spaceId, merged).catch(() => {});
    mutated = true;
  }

  if (mutated) {
    useSpaceEntityStore.setState((s) => ({
      caches: nextCaches,
      bumpVersion: s.bumpVersion + 1,
    }));
  }
}

function _applyDeleteToCaches(entityId: string): void {
  const state = useSpaceEntityStore.getState();
  let mutated = false;
  const nextCaches = new Map(state.caches);
  for (const [spaceId, entry] of nextCaches) {
    if (!entry.entityById.has(entityId)) continue;
    const nextById = new Map(entry.entityById);
    nextById.delete(entityId);
    const nextList = entry.entities.filter(
      (e) => e._id !== entityId && (e as any).id !== entityId,
    );
    nextCaches.set(spaceId, { ...entry, entities: nextList, entityById: nextById });
    mutated = true;
  }
  if (mutated) {
    useSpaceEntityStore.setState((s) => ({
      caches: nextCaches,
      bumpVersion: s.bumpVersion + 1,
    }));
  }
}

function _getByIdAcrossCaches(id: string): BaseEntityType | undefined {
  for (const entry of useSpaceEntityStore.getState().caches.values()) {
    const e = entry.entityById.get(id);
    if (e) return e;
  }
  return undefined;
}

function _getByTypeAcrossCaches(entityType: string): BaseEntityType[] {
  const out: BaseEntityType[] = [];
  const seen = new Set<string>();
  for (const entry of useSpaceEntityStore.getState().caches.values()) {
    for (const e of entry.entities) {
      if (e.entityType !== entityType) continue;
      const id = e._id || (e as any).id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
  }
  return out;
}

function _dumpAllSpaceEntities(): BaseEntityType[] {
  const out: BaseEntityType[] = [];
  const seen = new Set<string>();
  for (const entry of useSpaceEntityStore.getState().caches.values()) {
    for (const e of entry.entities) {
      const id = e._id || (e as any).id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
  }
  return out;
}

export async function dropSpaceEntityCache(spaceId: string): Promise<void> {
  useSpaceEntityStore.getState().evictSpace(spaceId);
  await deleteSnapshot(spaceId);
}

/**
 * Live-update wiring is now driven by the entity registry (see the
 * `registerScopeAdapter('space', ...)` call below). The orgYDocStore live-
 * update bus calls `entityRegistry.upsert / deleteEntity` which dispatches
 * straight into our adapter; no separate subscription handoff is needed.
 *
 * This export is retained as an idempotent no-op so existing callers (App.tsx,
 * entitySystemYjsStore.initEntitySystemYjsStore) don't need to be changed in
 * the same commit as the registry rollout. It has no behavior.
 *
 * The adapter is registered when this module is first imported. That happens
 * during the React boot path because the router and most consumers transitively
 * import spaceEntityStore — guaranteeing the dispatcher is wired before the
 * org Y.Doc starts emitting updates.
 */
export function installSpaceEntityLiveUpdates(): void {
  // intentionally empty — registration happens at module load via
  // registerScopeAdapter('space', ...) below.
}

// Register the adapter with the entity registry so the live-update bus
// (orgYDocStore) and registry dispatchers route into us.
//
// Note: this adapter intentionally accepts entities of any type the registry
// dispatches to it. The registry has the authoritative say on routing; if it
// resolved an entity (e.g. Automation, Tasks.Task, BankTransaction) to the
// space adapter via the adapter-missing fallback, we must honor that and
// place the entity in the matching space cache.
registerScopeAdapter('space', {
  getById: _getByIdAcrossCaches,
  getByType: _getByTypeAcrossCaches,
  upsert: _applyUpsertToCaches,
  deleteEntity: _applyDeleteToCaches,
  dumpAll: _dumpAllSpaceEntities,
});

// Bridge bumpVersion ticks → entityRegistry.subscribeToChanges so app stores
// (mail / tasks / bank / …) can listen for "any entity changed" via the
// registry without subscribing directly to this store.
useSpaceEntityStore.subscribe((state, prev) => {
  if (state.bumpVersion === prev.bumpVersion) return;
  notifyRegistryChange();
});
